import React, { useState } from 'react';
import {
  DeleteButton, EditButton, Show, Tab, TabbedShowLayout, TopToolbar,
  useNotify, useRecordContext, useRedirect, useRefresh,
} from 'react-admin';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import CasinoIcon from '@mui/icons-material/Casino';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import CheckChainUsesModule, { ModuleKey } from '../../components/CheckChainUsesModule';
import Permission from '../../components/Permission';
import { DEFAULT_API_ADMIN, PERMISSIONS } from '../../constants';
import { getChainId, httpClientReactAdmin } from '../../lib';
import { EVENTS, track } from '../../lib/analytics';
import { GiftProductHelper, GiftProductType, GiftRuleType } from '../../models';
import {
  ROLETA_ACTIVATION_ERRORS, ROLETA_RESOURCE, RoletaSegment, RoletaSegmentKind, RoletaStatus,
} from '../../models/roleta';
import RoletaSpinsTab from './RoletaSpinsTab';
import RoletaStatusChip from './RoletaStatusChip';

type RoletaAction = 'activate' | 'inactivate' | 'reactivate' | 'close';

const fmtNum = (value: unknown) =>
  value === null || value === undefined ? '—' : Number(value).toLocaleString('pt-BR');

const fmtBrl = (value: unknown) =>
  value === null || value === undefined
    ? '—'
    : Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const fmtPct = (value: number | null) =>
  value === null ? '—' : `${(value * 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;

const fmtDateTime = (value?: string | null) => (value ? new Date(value).toLocaleString('pt-BR') : '—');

const Title = () => {
  const record = useRecordContext();
  if (!record) return null;
  return (
    <Box display="flex" alignItems="center" gap={2}>
      <Typography variant="h6" component="span">{record.name}</Typography>
      <RoletaStatusChip status={record.status} />
    </Box>
  );
};

const ShowActions = () => {
  const record = useRecordContext();
  const status = record?.status as RoletaStatus | undefined;
  return (
    <TopToolbar>
      {status !== RoletaStatus.Closed && <EditButton />}
      {status === RoletaStatus.Draft && (
        <DeleteButton
          mutationMode="pessimistic"
          confirmTitle="Excluir roleta"
          confirmContent="A roleta em rascunho será removida. Esta ação não pode ser desfeita."
        />
      )}
    </TopToolbar>
  );
};

const StatCard: React.FC<{ label: string; value: string; hint?: string; color?: string }> = ({ label, value, hint, color }) => (
  <Card sx={{ flex: 1, minWidth: 180 }}>
    <CardContent>
      <Typography variant="overline" color="text.secondary">{label}</Typography>
      <Typography variant="h5" fontWeight={700} sx={{ color }}>{value}</Typography>
      {hint && <Typography variant="caption" color="text.secondary">{hint}</Typography>}
    </CardContent>
  </Card>
);

/** Atalhos para os dois brindes que a roleta precisa (padrão CloneGiftButton). */
const GiftShortcuts = () => {
  const record = useRecordContext();
  const redirect = useRedirect();
  if (!record) return null;

  const goToGiftCreate = (state: any) => {
    redirect('/gifts/create', undefined, undefined, undefined, state);
  };

  const createSpinGift = () => goToGiftCreate({
    name: `Giro na Roleta — ${record.name}`,
    products: [GiftProductHelper.createGiftProduct({
      type: GiftProductType.SpinTicket,
      roletaId: String(record.id),
      roletaName: record.name as string,
      spinAmount: 1,
    })],
  });

  const createPrizeGift = () => goToGiftCreate({
    name: `Prêmios da Roleta — ${record.name}`,
    giftRules: [{
      type: GiftRuleType.roleta_prize,
      groupId: 0,
      roletaId: String(record.id),
      roletaName: record.name as string,
      stationIds: [],
    }],
  });

  return (
    <Card sx={{ mt: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>Brindes desta roleta</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          A roleta precisa de dois brindes no Clube de Vantagens: um que <strong>dá o giro</strong> ao cliente
          (por abastecimento, compra na loja, cadastro…) e outro que guarda os <strong>prêmios</strong> que as fatias entregam.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <Button variant="outlined" startIcon={<CasinoIcon />} onClick={createSpinGift}>
            Criar brinde de giro
          </Button>
          <Button variant="outlined" startIcon={<CardGiftcardIcon />} onClick={createPrizeGift}>
            Criar brinde de prêmios
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
};

const StatusActions = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const chainId = getChainId();

  const [pendingAction, setPendingAction] = useState<RoletaAction | null>(null);
  const [running, setRunning] = useState(false);
  const [activationError, setActivationError] = useState<string | null>(null);

  if (!record) return null;

  const status = record.status as RoletaStatus;

  const apiCall = async (action: RoletaAction) => {
    const url = `${DEFAULT_API_ADMIN}/chains/${chainId}/roletas/${record.id}/${action}`;
    try {
      const { json } = await httpClientReactAdmin(url, { method: 'POST', body: JSON.stringify({}) });
      // O backend embrulha tudo em createResponse() → { object, data }
      return json?.data ?? json;
    } catch (err: any) {
      const code: string | undefined = err?.body?.error;
      const apiMessage = err?.body?.message
        || (code ? ROLETA_ACTIVATION_ERRORS[code] : undefined)
        || err?.message
        || 'Erro na chamada';
      throw new Error(apiMessage);
    }
  };

  const MESSAGES: Record<RoletaAction, string> = {
    activate: 'Roleta ativada! Ela já aparece no aplicativo.',
    inactivate: 'Roleta inativada. Ela some do aplicativo e para de conceder giro.',
    reactivate: 'Roleta reativada.',
    close: 'Roleta encerrada.',
  };

  const handleConfirm = async () => {
    if (!pendingAction) return;
    setRunning(true);
    setActivationError(null);
    try {
      await apiCall(pendingAction);
      if (pendingAction === 'activate') {
        track(EVENTS.ROLETA_ACTIVATED, { chain_id: chainId });
      }
      notify(MESSAGES[pendingAction], { type: 'success' });
      setPendingAction(null);
      refresh();
    } catch (err: any) {
      if (pendingAction === 'activate') {
        setActivationError(err?.message || 'Erro ao ativar');
      } else {
        notify(err?.message || 'Erro na operação', { type: 'error' });
        setPendingAction(null);
      }
    } finally {
      setRunning(false);
    }
  };

  const closeDialog = () => {
    setPendingAction(null);
    setActivationError(null);
  };

  const DIALOGS: Record<RoletaAction, { title: string; body: React.ReactNode; severity: 'info' | 'warning' }> = {
    activate: {
      title: 'Ativar roleta',
      severity: 'info',
      body: 'A roleta passa a aparecer no aplicativo e os brindes vinculados começam a conceder giros.',
    },
    inactivate: {
      title: 'Inativar roleta',
      severity: 'warning',
      body: 'A roleta some do aplicativo e para de conceder giros novos. Os giros que os clientes já ganharam continuam válidos e podem ser usados. Você pode reativar depois.',
    },
    reactivate: {
      title: 'Reativar roleta',
      severity: 'info',
      body: 'A roleta volta a aparecer no aplicativo e a conceder giros. Se a data de fim já passou, ela será encerrada automaticamente em instantes.',
    },
    close: {
      title: 'Encerrar roleta',
      severity: 'warning',
      body: 'A roleta é encerrada em definitivo: não volta a ficar ativa e os giros pendentes vencem. Prêmios já entregues não são revertidos.',
    },
  };

  const dialog = pendingAction ? DIALOGS[pendingAction] : null;

  return (
    <>
      {status === RoletaStatus.Draft && (
        <Card sx={{ mt: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Ativar roleta</Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              Antes de ativar, confira:
              <br />1️⃣ As fatias estão montadas (pelo menos 2 ativas, uma delas com estoque ilimitado).
              <br />2️⃣ Existe um brinde que <strong>dá o giro</strong> vinculado a esta roleta.
              <br />3️⃣ Existe um brinde de <strong>prêmios</strong> vinculado, se alguma fatia entrega prêmio.
            </Alert>
            <Button variant="contained" color="primary" onClick={() => setPendingAction('activate')}>
              Ativar roleta
            </Button>
          </CardContent>
        </Card>
      )}

      {status === RoletaStatus.Active && (
        <Card sx={{ mt: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Roleta no ar</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Inativar tira a roleta do aplicativo sem perder nada — dá para reativar. Encerrar é definitivo.
            </Typography>
            <Stack direction="row" spacing={2}>
              <Button variant="outlined" color="warning" onClick={() => setPendingAction('inactivate')}>
                Inativar
              </Button>
              <Button variant="outlined" color="error" onClick={() => setPendingAction('close')}>
                Encerrar
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}

      {status === RoletaStatus.Inactive && (
        <Card sx={{ mt: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Roleta inativa</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Esta roleta não aparece no aplicativo e não concede giros novos. Os giros já concedidos continuam valendo.
            </Typography>
            <Button variant="contained" color="primary" onClick={() => setPendingAction('reactivate')}>
              Reativar roleta
            </Button>
          </CardContent>
        </Card>
      )}

      {status === RoletaStatus.Closed && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Roleta encerrada — a tela fica só para consulta.
        </Alert>
      )}

      <Dialog open={!!pendingAction} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{dialog?.title}</DialogTitle>
        <DialogContent>
          <Alert severity={dialog?.severity ?? 'info'}>{dialog?.body}</Alert>
          {activationError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              <Typography variant="body2" fontWeight={600} gutterBottom>Falta ajustar antes de ativar</Typography>
              <Typography variant="body2">{activationError}</Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancelar</Button>
          <Button variant="contained" onClick={handleConfirm} disabled={running}>
            {running ? <CircularProgress size={20} /> : 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const DetailsTabContent = () => {
  const record = useRecordContext();
  if (!record) return null;

  const stats = record.stats || {};
  const segments: RoletaSegment[] = record.segments || [];
  const enabledSegments = segments.filter((segment) => segment.isEnabled);

  return (
    <Box sx={{ width: '100%' }}>
      <Card sx={{ mb: 2, overflow: 'hidden' }}>
        {record.imageUrl && (
          <Box sx={{ height: 160, width: '100%', backgroundImage: `url(${record.imageUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
        )}
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} justifyContent="space-between">
            <Box>
              <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                <Typography variant="h6" fontWeight={600}>{record.name}</Typography>
                <RoletaStatusChip status={record.status} />
              </Box>
              {record.description ? (
                <Typography variant="body2" color="text.secondary">{record.description}</Typography>
              ) : (
                <Typography variant="body2" color="text.disabled">Sem descrição</Typography>
              )}
            </Box>
            <Box textAlign={{ sm: 'right' }}>
              <Typography variant="caption" color="text.secondary" display="block">Período</Typography>
              <Typography variant="body2" fontWeight={500}>
                {fmtDateTime(record.startAt)} até {fmtDateTime(record.endAt)}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                {record.spinValidityDays
                  ? `Cada giro vale ${record.spinValidityDays} ${record.spinValidityDays === 1 ? 'dia' : 'dias'}`
                  : 'Cada giro vale até o fim da roleta'}
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <StatCard label="Giros concedidos" value={fmtNum(stats.spinsGranted)} hint={`${fmtNum(stats.spinsAvailable)} ainda não usados`} />
        <StatCard label="Giros realizados" value={fmtNum(stats.spinsUsed)} hint={`${fmtNum(stats.spinsExpired)} venceram sem girar`} />
        <StatCard label="Prêmios entregues" value={fmtNum(stats.prizesDelivered)} hint={`${fmtNum(stats.prizesPending)} sendo liberados`} color="#2e7d32" />
        <StatCard label="Custo" value={fmtBrl(stats.totalCost)} hint={`${fmtBrl(stats.costPerSpin)} por giro`} />
      </Stack>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="overline" color="text.secondary">A roda</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
            {enabledSegments.length === 0 && (
              <Typography variant="body2" color="text.disabled">
                Nenhuma fatia ativa. Abra a edição para montar a roda.
              </Typography>
            )}
            {enabledSegments.map((segment) => (
              <Chip
                key={segment.id ?? `segment-${segment.segmentIndex}`}
                size="small"
                sx={{ mb: 1 }}
                label={`${segment.icon ? `${segment.icon} ` : ''}${segment.label}`}
                color={segment.kind === RoletaSegmentKind.Prize ? 'primary' : 'default'}
                variant={segment.kind === RoletaSegmentKind.Prize ? 'filled' : 'outlined'}
              />
            ))}
          </Stack>
          {record.regulationUrl && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
              Regulamento: <a href={record.regulationUrl} target="_blank" rel="noreferrer">{record.regulationUrl}</a>
            </Typography>
          )}
        </CardContent>
      </Card>

      <StatusActions />
      <GiftShortcuts />
    </Box>
  );
};

const SegmentsTabContent = () => {
  const record = useRecordContext();
  if (!record) return null;

  const segments: RoletaSegment[] = record.segments || [];
  const stats = record.stats || {};
  const spinsUsed: number = Number(stats.spinsUsed || 0);
  const totalWeight = segments
    .filter((segment) => segment.isEnabled)
    .reduce((sum, segment) => sum + Math.max(0, segment.weight || 0), 0);

  if (segments.length === 0) {
    return (
      <Box p={4} textAlign="center">
        <Typography color="text.secondary">Nenhuma fatia cadastrada ainda.</Typography>
      </Box>
    );
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Fatia</TableCell>
            <TableCell>Tipo</TableCell>
            <TableCell align="center">Peso</TableCell>
            <TableCell align="center">% configurado</TableCell>
            <TableCell align="center">% real</TableCell>
            <TableCell align="center">Vezes sorteada</TableCell>
            <TableCell align="center">Estoque restante</TableCell>
            <TableCell align="center">Custo</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {segments.map((segment) => {
            const winsCount = segment.winsCount ?? 0;
            const configuredPct = segment.isEnabled && totalWeight > 0
              ? Math.max(0, segment.weight || 0) / totalWeight
              : null;
            const realPct = spinsUsed > 0 ? winsCount / spinsUsed : null;
            const remaining = segment.maxWins === null || segment.maxWins === undefined
              ? 'Ilimitado'
              : fmtNum(Math.max(0, segment.maxWins - winsCount));
            const cost = (segment.estimatedCost ?? 0) * winsCount;

            return (
              <TableRow key={segment.id ?? `segment-${segment.segmentIndex}`} hover>
                <TableCell>
                  {segment.icon ? `${segment.icon} ` : ''}{segment.label}
                  {!segment.isEnabled && (
                    <Chip size="small" label="Desativada" sx={{ ml: 1 }} />
                  )}
                  {segment.giftProductName && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {segment.giftProductName}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>{RoletaSegmentKind.translateKind(segment.kind)}</TableCell>
                <TableCell align="center">{fmtNum(segment.weight)}</TableCell>
                <TableCell align="center">{fmtPct(configuredPct)}</TableCell>
                <TableCell align="center">{fmtPct(realPct)}</TableCell>
                <TableCell align="center">{fmtNum(winsCount)}</TableCell>
                <TableCell align="center">{remaining}</TableCell>
                <TableCell align="center">{cost > 0 ? fmtBrl(cost) : '—'}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const RoletaShow: React.FC = (props: any) => (
  <CheckChainUsesModule module={ModuleKey.ROLETA}>
    <Permission permission={PERMISSIONS.GIFT}>
      <Show {...props} resource={ROLETA_RESOURCE} title={<Title />} actions={<ShowActions />}>
        <TabbedShowLayout>
          <Tab label="Detalhes">
            <DetailsTabContent />
          </Tab>
          <Tab label="Giros" path="spins">
            <RoletaSpinsTab />
          </Tab>
          <Tab label="Prêmios por fatia" path="segments">
            <SegmentsTabContent />
          </Tab>
        </TabbedShowLayout>
      </Show>
    </Permission>
  </CheckChainUsesModule>
);

export default RoletaShow;
